import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ChevronLeft, Loader2, Clock, Upload, AlertCircle, Send, Check, X } from "lucide-react";
import { toast } from "sonner";
import { compressImageFile, approxBase64Bytes } from "../utils/imageCompress";

/**
 * P2P Trade detail page.
 * Behavior splits by trade.country:
 *  India   -> No chat. Bank account shown post-KYC. Screenshot+UTR upload mandatory.
 *  Pakistan-> Chat enabled. Phone numbers stripped in chat (backend rejects).
 */
const P2PTradePage = () => {
  const { tradeId } = useParams();
  const { user, authAxios } = useAuth();
  const navigate = useNavigate();
  const api = authAxios();
  const meId = user?.id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Payment proof
  const [screenshotB64, setScreenshotB64] = useState("");
  const [utr, setUtr] = useState("");
  const [compressingScreenshot, setCompressingScreenshot] = useState(false);

  // Chat (Pakistan)
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const chatBottomRef = useRef(null);

  // Live countdown
  const [secondsLeft, setSecondsLeft] = useState(0);

  const fetchAll = async () => {
    try {
      const trade = await api.get(`/p2p/trades/${tradeId}`);
      setData(trade.data);
      const tCountry = trade.data?.trade?.country;
      if (tCountry === "pakistan" || tCountry === "nigeria") {
        const chatRes = await api.get(`/p2p/trades/${tradeId}/chat`);
        setMessages(chatRes.data.messages || []);
      }
    } catch (e) {
      console.error(e);
      // Skip noisy toasts on auth expiry — interceptor handles redirect
      if (!e.isAuthExpiry && e.response?.status !== 401) {
        toast.error(e.response?.data?.detail || "Failed to load trade");
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, 10000); // poll every 10s
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [tradeId]);

  // Timer
  useEffect(() => {
    if (!data?.trade?.timer_expires_at) return;
    const tick = () => {
      const end = new Date(data.trade.timer_expires_at).getTime();
      const left = Math.max(0, Math.floor((end - Date.now()) / 1000));
      setSecondsLeft(left);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [data?.trade?.timer_expires_at]);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCompressingScreenshot(true);
    try {
      const originalKb = Math.round(f.size / 1024);
      const dataUrl = await compressImageFile(f);
      const finalKb = Math.round(approxBase64Bytes(dataUrl) / 1024);
      setScreenshotB64(dataUrl);
      toast.success(`Screenshot: ${originalKb} KB → ${finalKb} KB`);
    } catch (err) {
      toast.error("Image compress failed: " + err.message);
    }
    setCompressingScreenshot(false);
  };

  const submitPaymentProof = async () => {
    if (!screenshotB64) {
      toast.error("Please upload payment screenshot");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/p2p/trades/${tradeId}/payment-proof`, {
        screenshot_b64: screenshotB64,
        utr_number: utr,
      });
      toast.success("Payment proof submitted. Waiting for seller to release USDT.");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit proof");
    }
    setBusy(false);
  };

  // Inline confirmation: first click sets pending=action, second click executes; auto-clears in 4s
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");

  const guardConfirm = (action) => {
    if (pendingConfirm !== action) {
      setPendingConfirm(action);
      setTimeout(() => setPendingConfirm((cur) => (cur === action ? null : cur)), 4000);
      return false;
    }
    setPendingConfirm(null);
    return true;
  };

  const releaseUsdt = async () => {
    if (!guardConfirm("release")) {
      toast.info("Tap once more to CONFIRM release (USDT will go to buyer)");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/p2p/trades/${tradeId}/release`);
      toast.success("USDT released to buyer ✓");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Release failed");
    }
    setBusy(false);
  };

  const cancelTrade = async () => {
    if (!guardConfirm("cancel")) {
      toast.info("Tap once more to CANCEL this trade");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/p2p/trades/${tradeId}/cancel`);
      toast.success("Trade cancelled");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    }
    setBusy(false);
  };

  const submitDispute = async () => {
    const reason = disputeReason.trim();
    if (reason.length < 5) {
      toast.error("Please describe the issue (min 5 characters)");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/p2p/trades/${tradeId}/dispute`, { reason });
      toast.success("Dispute raised. It will be reviewed shortly.");
      setDisputeOpen(false);
      setDisputeReason("");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dispute failed");
    }
    setBusy(false);
  };

  const sendMessage = async () => {
    const text = chatInput.trim();
    if (!text) return;
    try {
      await api.post(`/p2p/trades/${tradeId}/chat`, { message: text });
      setChatInput("");
      const r = await api.get(`/p2p/trades/${tradeId}/chat`);
      setMessages(r.data.messages || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
  };

  if (loading || !data) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }

  const trade = data.trade;
  const isIndia = trade.country === "india";
  const isNigeria = trade.country === "nigeria";
  const isPakistan = trade.country === "pakistan";
  const chatEnabled = isPakistan || isNigeria;
  const isBuyer = meId && meId === trade.buyer_id;
  const isSeller = meId && meId === trade.seller_id;
  const status = trade.status;
  const counterparty = data.counterparty;
  const paymentInfo = data.payment_info;
  const countryFlag = isIndia ? "🇮🇳 India" : isNigeria ? "🇳🇬 Nigeria" : "🇵🇰 Pakistan";

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border flex items-center px-4 py-3">
        <button onClick={() => navigate("/p2p/my-trades")} className="p-1 mr-2" data-testid="trade-back-btn">
          <ChevronLeft size={24} />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold">Trade #{tradeId.slice(0, 8)}</h1>
          <div className="text-xs text-muted-foreground">
            <span className="font-mono">{counterparty?.code_name}</span> ({countryFlag})
          </div>
        </div>
        <StatusBadge status={status} />
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Counterparty real-name banner — verify against bank holder */}
        <div className="bg-gradient-to-r from-primary/10 to-purple-500/10 border border-primary/30 rounded-xl p-3" data-testid="counterparty-real-name">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {isBuyer ? "Sending money to" : "Receiving money from"}
          </div>
          <div className="text-lg font-bold text-primary">{counterparty?.real_name}</div>
          <div className="text-[10px] text-muted-foreground font-mono">
            Code: {counterparty?.code_name} • ID: {counterparty?.id_short}
          </div>
          {isBuyer && (isIndia || isNigeria) && (
            <div className="text-[10px] mt-1 text-yellow-400">
              ⚠️ {isNigeria ? "Payment account" : "Bank account"} holder name must match: <b>{counterparty?.real_name}</b>
            </div>
          )}
        </div>

        {/* Trade summary */}
        <div className="bg-card border border-border rounded-xl p-4 space-y-2">
          <Row label="Amount" value={`${trade.amount_usdt} USDT`} />
          <Row label="Price" value={`${trade.price_per_usdt} ${trade.fiat}/USDT`} />
          <Row label={`Pay ${trade.fiat}`} value={`${trade.amount_fiat.toFixed(2)} ${trade.fiat}`} highlight />
          <Row label="USDT credited to" value={isBuyer ? (trade.source_buyer === "outside" ? "Total Deposited (auto-stake)" : "Game Wallet") : "—"} />
        </div>

        {/* Timer */}
        {(status === "awaiting_payment" || status === "pending_kyc") && secondsLeft > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 flex items-center gap-2 text-sm">
            <Clock size={18} className="text-yellow-400" />
            <span>Time left: <b>{mins}:{secs.toString().padStart(2, "0")}</b></span>
          </div>
        )}

        {/* Status-specific UI */}
        {status === "pending_kyc" && isBuyer && (
          <div className="bg-card border border-border rounded-xl p-4 space-y-3">
            <div className="text-sm font-semibold flex items-center gap-2">
              <AlertCircle size={16} className="text-yellow-400" /> KYC Required
            </div>
            <div className="text-xs text-muted-foreground">
              Aadhar + selfie + phone verification needed before bank details are revealed.
            </div>
            <button
              onClick={() => navigate(`/p2p/kyc/${tradeId}`)}
              className="w-full py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold"
              data-testid="goto-kyc-btn"
            >
              Submit KYC Documents
            </button>
          </div>
        )}

        {status === "pending_kyc" && isSeller && (
          <div className="bg-card border border-border rounded-xl p-4 text-xs text-muted-foreground">
            Waiting for buyer to complete KYC verification before bank details are shared.
          </div>
        )}

        {/* Bank/payment details after reveal */}
        {paymentInfo && (
          <div className="bg-card border border-border rounded-xl p-4 space-y-2">
            <div className="text-sm font-semibold border-l-4 border-primary pl-2">
            {isIndia ? "Bank Account (NEFT/IMPS only)" : isNigeria ? "Payment Details (Nigeria)" : "Payment Methods"}
            </div>
            {isIndia && paymentInfo.bank_details && (
              <div className="text-sm space-y-1">
                <Row label="Account No" value={paymentInfo.bank_details.account_number} copy />
                <Row label="IFSC" value={paymentInfo.bank_details.ifsc} copy />
                <Row label="Holder" value={paymentInfo.bank_details.holder_name} />
                <Row label="Bank" value={paymentInfo.bank_details.bank_name} />
              </div>
            )}
            {!isIndia && (paymentInfo.payment_methods || []).map((pm, i) => (
              <div key={i} className="text-sm border-b border-border/50 pb-2 mb-2 space-y-1">
                <div className="text-xs uppercase text-primary">{pm.method?.replace("_", " ")}</div>
                <Row label="Account" value={pm.account_number} copy />
                <Row label="Holder" value={pm.holder_name} />
                {pm.extra_info && <Row label={isNigeria && pm.method === "bank_transfer" ? "Bank" : "Info"} value={pm.extra_info} />}
              </div>
            ))}
            {paymentInfo.notes && <div className="text-xs italic text-muted-foreground pt-2">Notes: {paymentInfo.notes}</div>}
          </div>
        )}

        {/* Buyer: submit payment proof */}
        {status === "awaiting_payment" && isBuyer && (
          <div className="bg-card border border-border rounded-xl p-4 space-y-3">
            <div className="text-sm font-semibold">After Sending Payment</div>
            <div className="text-xs text-muted-foreground">Upload screenshot + UTR/transaction reference</div>
            <div className="relative min-h-[60px]">
              <input type="file" accept="image/*" onChange={handleFile} disabled={compressingScreenshot} className="text-xs" data-testid="screenshot-input" />
              {compressingScreenshot && (
                <div className="absolute inset-0 bg-background/95 backdrop-blur-sm rounded-xl flex flex-col items-center justify-center gap-2 z-10 py-6" data-testid="compression-overlay">
                  <div className="w-14 h-14 rounded-full border-4 border-yellow-500/30 border-t-yellow-400 animate-spin"></div>
                  <div className="text-sm font-semibold text-yellow-400">Compressing screenshot...</div>
                  <div className="text-[10px] text-muted-foreground text-center px-2">
                    Reducing size for faster upload<br />
                    <span className="text-yellow-500/80">Please wait, do not close...</span>
                  </div>
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce"></span>
                    <span className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: "0.15s" }}></span>
                    <span className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: "0.3s" }}></span>
                  </div>
                </div>
              )}
              {screenshotB64 && !compressingScreenshot && <img src={screenshotB64} alt="proof" className="rounded-lg max-h-48 mx-auto mt-2" />}
            </div>
            <input
              value={utr}
              onChange={(e) => setUtr(e.target.value)}
              placeholder="UTR / Reference number (optional)"
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
              data-testid="utr-input"
            />
            <button
              onClick={submitPaymentProof}
              disabled={busy || !screenshotB64 || compressingScreenshot}
              className="w-full py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold disabled:opacity-50"
              data-testid="submit-payment-proof-btn"
            >
              {busy ? <Loader2 className="animate-spin inline" /> : (<><Upload size={14} className="inline mr-1" />Mark Payment Done</>)}
            </button>
          </div>
        )}

        {/* Seller: review proof + release */}
        {status === "payment_done" && isSeller && (
          <div className="bg-card border border-border rounded-xl p-4 space-y-3">
            <div className="text-sm font-semibold">Verify Payment & Release</div>
            {trade.payment_screenshot_b64 && (
              <img src={trade.payment_screenshot_b64} alt="proof" className="rounded-lg max-h-64 mx-auto" />
            )}
            {trade.utr_number && (
              <div className="text-sm">UTR: <span className="font-mono font-semibold">{trade.utr_number}</span></div>
            )}
            <div className="text-xs text-muted-foreground">
              Check your bank statement. Only release USDT after confirming payment received.
            </div>
            <button
              onClick={releaseUsdt}
              disabled={busy}
              className={`w-full py-3 rounded-lg font-bold disabled:opacity-50 transition ${
                pendingConfirm === "release"
                  ? "bg-yellow-500 text-black animate-pulse"
                  : "bg-green-600 text-white"
              }`}
              data-testid="release-usdt-btn"
            >
              {busy ? (
                <Loader2 className="animate-spin inline" />
              ) : pendingConfirm === "release" ? (
                "⚠️ Tap Again to Confirm Release"
              ) : (
                <><Check size={16} className="inline mr-1" />Release USDT</>
              )}
            </button>
            <button
              onClick={() => setDisputeOpen(true)}
              disabled={busy}
              className="w-full py-2 border border-red-500/50 text-red-400 rounded-lg text-sm"
              data-testid="dispute-btn-seller"
            >
              Raise Dispute (payment not received)
            </button>
          </div>
        )}

        {/* Buyer waiting for release */}
        {status === "payment_done" && isBuyer && (
          <div className="bg-card border border-border rounded-xl p-4 space-y-3">
            <div className="text-sm text-muted-foreground">
              Payment marked done. Waiting for seller to verify & release USDT.
            </div>
            <button
              onClick={() => setDisputeOpen(true)}
              disabled={busy}
              className="w-full py-2 border border-red-500/50 text-red-400 rounded-lg text-sm"
              data-testid="dispute-btn-buyer"
            >
              Raise Dispute (seller not releasing)
            </button>
          </div>
        )}

        {/* Cancel option (early states) */}
        {(status === "awaiting_payment" || status === "pending_kyc") && (
          <button
            onClick={cancelTrade}
            disabled={busy}
            className={`w-full py-2 rounded-lg text-xs transition ${
              pendingConfirm === "cancel"
                ? "bg-red-600 text-white font-bold animate-pulse"
                : "border border-border text-muted-foreground"
            }`}
            data-testid="cancel-trade-btn"
          >
            {pendingConfirm === "cancel" ? "⚠️ Tap Again to Cancel" : "Cancel Trade"}
          </button>
        )}

        {/* Completed / cancelled */}
        {status === "completed" && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center text-green-400">
            <Check size={32} className="mx-auto mb-2" />
            Trade Completed Successfully
          </div>
        )}
        {status === "cancelled" && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center text-red-400">
            <X size={32} className="mx-auto mb-2" />
            Trade Cancelled
          </div>
        )}
        {status === "disputed" && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 text-center text-yellow-400">
            <AlertCircle size={32} className="mx-auto mb-2" />
            Under Review
          </div>
        )}

        {/* Chat (Pakistan + Nigeria) */}
        {chatEnabled && status !== "completed" && status !== "cancelled" && (
          <div className="bg-card border border-border rounded-xl p-3 space-y-2">
            <div className="text-sm font-semibold">Chat with <span className="font-mono text-primary">{counterparty?.code_name}</span></div>
            <div className="h-64 overflow-y-auto space-y-2 pr-1">
              {messages.length === 0 && (
                <div className="text-xs text-muted-foreground text-center py-8">No messages yet</div>
              )}
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${m.sender_id === meId ? "ml-auto bg-primary/20" : "bg-background border border-border"}`}
                >
                  {m.message}
                  <div className="text-[9px] text-muted-foreground mt-1">{new Date(m.created_at).toLocaleTimeString()}</div>
                </div>
              ))}
              <div ref={chatBottomRef}></div>
            </div>
            <div className="flex gap-2 pt-2 border-t border-border">
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Type a message... (no phone numbers allowed)"
                className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm"
                data-testid="chat-input"
              />
              <button onClick={sendMessage} className="px-3 bg-primary text-primary-foreground rounded-lg" data-testid="chat-send-btn">
                <Send size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Dispute Modal */}
      {disputeOpen && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setDisputeOpen(false)}
        >
          <div
            className="bg-card border border-border rounded-2xl w-full max-w-md p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
            data-testid="dispute-modal"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-red-400">Raise Dispute</h2>
              <button onClick={() => setDisputeOpen(false)} className="p-1" data-testid="close-dispute-modal">
                <X size={20} />
              </button>
            </div>
            <div className="text-xs text-muted-foreground">
              Describe the issue clearly so it can be reviewed quickly. Include any reference numbers.
            </div>
            <textarea
              value={disputeReason}
              onChange={(e) => setDisputeReason(e.target.value)}
              rows={4}
              placeholder="What went wrong? (min 5 characters)"
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
              data-testid="dispute-reason-input"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={() => setDisputeOpen(false)}
                className="flex-1 py-2 border border-border rounded-lg text-sm"
                data-testid="dispute-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={submitDispute}
                disabled={busy || disputeReason.trim().length < 5}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
                data-testid="dispute-submit-btn"
              >
                {busy ? <Loader2 className="animate-spin inline" /> : "Submit Dispute"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value, copy = false, highlight = false }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className={`font-mono ${highlight ? "text-primary font-bold text-base" : ""}`}>
      {value}
      {copy && (
        <button
          onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copied"); }}
          className="ml-2 text-[10px] text-primary"
        >Copy</button>
      )}
    </span>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    pending_kyc: { c: "bg-yellow-500/20 text-yellow-400", t: "KYC Pending" },
    awaiting_payment: { c: "bg-blue-500/20 text-blue-400", t: "Awaiting Payment" },
    payment_done: { c: "bg-purple-500/20 text-purple-400", t: "Payment Done" },
    completed: { c: "bg-green-500/20 text-green-400", t: "Completed" },
    cancelled: { c: "bg-red-500/20 text-red-400", t: "Cancelled" },
    disputed: { c: "bg-orange-500/20 text-orange-400", t: "Disputed" },
  };
  const s = map[status] || { c: "bg-muted", t: status };
  return <span className={`text-[10px] px-2 py-1 rounded-full ${s.c}`} data-testid="trade-status-badge">{s.t}</span>;
};

export default P2PTradePage;
